import { getCalendarViewTypeMenuItems } from './viewSwitcher/getCalendarViewTypeMenuItems';
import { getSurfaceConfigMenuItems } from './viewSwitcher/getSurfaceConfigMenuItems';
import { getSurfaceCreateSaveViewMenuItems } from './viewSwitcher/getSurfaceCreateSaveViewMenuItems';
import { getSurfaceSavedViewMenuItems } from './viewSwitcher/getSurfaceSavedViewMenuItems';
import { getViewSwitcherSubMenuProps } from './viewSwitcher/getViewSwitcherSubMenuProps';
import type { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { assertNever } from 'owa-assert';
import { getSelectedSaveView, hasSelectedSaveView } from 'owa-calendar-module-commanding-utils';
import { getSelectedViewType, isBoardViewTypeSelected } from 'owa-calendar-module-store';
import { getNumDaysInDayRange } from 'owa-calendar-surface-options-utils';
import { CalendarViewTypeEnum } from 'owa-calendar-view-type';
import { CharmControl, CharmControlColorPattern } from 'owa-charmcontrol';
import { DisplayOption, FilterableMenuItem, FilterableMenuItemArgs } from 'owa-filterable-menu';
import { getPalette } from 'owa-theme';
import * as React from 'react';
import {
    getBoardNameViewTypeDefinition,
    getDayViewTypeDefinition,
    getMultiDayViewTypeDefinition,
    getMonthViewTypeDefinition,
    getSavedViewTypeDefinition,
    getWeekViewTypeDefinition,
    getWorkWeekViewTypeDefinition,
    ViewTypeDefinition,
} from './viewSwitcher/viewTypeDefinitions';
import { Icon } from '@fluentui/react/lib/Icon';
import { ControlIcons } from 'owa-control-icons';

import styles from '../components/CalendarModuleCommandBar.scss';

export function getViewSwitcherMenuItem(onCustomRenderSubMenuChange: () => void) {
    const selectedSaveViewConfig = getSelectedSaveView();
    const hasSelectedSaveViewConfig: boolean = hasSelectedSaveView();

    const getCurrentViewTypeDefinition = (): ViewTypeDefinition => {
        if (hasSelectedSaveViewConfig) {
            return getSavedViewTypeDefinition(selectedSaveViewConfig);
        }

        const selectedViewType = getSelectedViewType();
        const numDaysInDayRange = getNumDaysInDayRange();
        switch (selectedViewType) {
            case CalendarViewTypeEnum.Daily:
                if (numDaysInDayRange === 1) {
                    return getDayViewTypeDefinition();
                } else {
                    return getMultiDayViewTypeDefinition(numDaysInDayRange);
                }
            case CalendarViewTypeEnum.WorkWeek:
                return getWorkWeekViewTypeDefinition();
            case CalendarViewTypeEnum.Weekly:
                return getWeekViewTypeDefinition();
            case CalendarViewTypeEnum.Monthly:
            case CalendarViewTypeEnum.Agenda: // not currently in use, but providing as graceful fallback
                return getMonthViewTypeDefinition();
            case CalendarViewTypeEnum.Board:
                return getBoardNameViewTypeDefinition();
            default:
                assertNever(selectedViewType);
        }
    };

    const palette = getPalette();
    const charmColorProps = {
        colorPattern: CharmControlColorPattern.Normal,
        calPrimary: palette.themePrimary,
        calDark: palette.themeDark,
        calDarkAlt: palette.themeDarkAlt,
        textColor: palette.neutralPrimary,
    };

    const viewSwitcherMenuItemList: IContextualMenuItem[] = [
        ...getCalendarViewTypeMenuItems(),
        ...getSurfaceSavedViewMenuItems(onCustomRenderSubMenuChange),
    ];

    if (!isBoardViewTypeSelected()) {
        viewSwitcherMenuItemList.push(
            ...getSurfaceConfigMenuItems(),
            ...getSurfaceCreateSaveViewMenuItems()
        );
    }

    return new FilterableMenuItem({
        ...getCurrentViewTypeDefinition(),
        [FilterableMenuItemArgs.displayOption]: DisplayOption.Left,
        [FilterableMenuItemArgs.key]: 'ViewSwitcher',
        [FilterableMenuItemArgs.subMenuProps]: getViewSwitcherSubMenuProps(
            viewSwitcherMenuItemList
        ),
        [FilterableMenuItemArgs.onRenderIcon]: hasSelectedSaveViewConfig
            ? () =>
                  parseInt(selectedSaveViewConfig.viewIcon) == 0 ? (
                      <Icon
                          iconName={ControlIcons.CircleFill}
                          className={styles.viewIcon}
                          styles={{ root: { color: palette.themePrimary, fontSize: '8px' } }}
                      />
                  ) : (
                      <CharmControl
                          iconId={parseInt(selectedSaveViewConfig.viewIcon)}
                          className={styles.charm}
                          colorProps={charmColorProps}
                      />
                  )
            : undefined,
    });
}
